import React, { memo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { StyHrDataGrid } from 'routes/HumanResourcesModule/components/HrDataGrid/HrDataGrid';
import { useSemiTreatmentOfSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/hooks/semiTreatment';
import { useGetHrGVTData } from './helpers/hooks/gvtData';
import { getColDefs } from './helpers/colDefinitions';
import HistoricalOrgChartHrContext from 'routes/HumanResourcesModule/Simulation/contexts/ObsoleteOrgChartSimulationContext';
import { getObsoleteGridProps } from '../../Agents/helpers/gridHelpers';
import { TRACKABLE_EVENTS_METADATA } from 'helpers/trackerHelpers';

type Props = {
    hrSimulation: HrSimulation;
};

const GVTDataGrid = ({ hrSimulation }: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { data: obsoleteOrgChartHr } = useContext(
        HistoricalOrgChartHrContext,
    );
    const {
        data: gvtDataResponse,
        isLoading: isHrSimulationResponseLoading,
        isFetching: isHrSimulationResponseFetching,
    } = useGetHrGVTData(hrSimulation.id);

    const gvtData = gvtDataResponse?.hrSimulationData?.data || []; // Fix in backend

    const { data: semiTreatmentHypothesesData = [] } =
        useSemiTreatmentOfSimulation(hrSimulation);

    const columnDefs = getColDefs({
        simulationStart: hrSimulation.startDate,
        simulationEnd: hrSimulation.endDate,
        semiTreatmentHypothesesData,
        simulationInitDate: hrSimulation.recurrentInitialisationStartDate,
    });
    const exportFileName = t('humanResources.simulation.gvtDataFileName', {
        title: hrSimulation.title,
    });
    const obsoleteGridProps = getObsoleteGridProps({
        hrSimulation,
        obsoleteOrgChartHr,
    });
    const gridProps = { ...obsoleteGridProps };

    return (
        <StyHrDataGrid
            trackEventMetadata={{
                gridName:
                    TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                        .gvtGrid,
            }}
            title={t('humanResources.simulation.scenarioArray')}
            infoTooltip={t('humanResources.simulation.scenarioMethod')}
            rowData={gvtData}
            columnDefs={columnDefs}
            exportFileName={exportFileName}
            isLoading={isHrSimulationResponseLoading}
            isFetching={isHrSimulationResponseFetching}
            gridProps={gridProps}
        />
    );
};

export default memo(GVTDataGrid);
